import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { buscarAnimalOffline, submitOffline } from '../../services/offlineHelpers';

const RegistrarServicioForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [hembra, setHembra] = useState(null);
  const [buscarIDE, setBuscarIDE] = useState('');
  const [buscandoHembra, setBuscandoHembra] = useState(false);

  const [formData, setFormData] = useState({
    hembra_id: '',
    fecha: new Date().toISOString().split('T')[0],
    tipo_servicio: 'IATF',
    toro_id: null,
    resultado: 'pendiente',
    fecha_confirmacion: '',
    dias_gestacion: '',
    temporada: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
    observaciones: '',
  });

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  const buscarHembraPorRFID = async () => {
    if (!buscarIDE.trim()) return;
    setBuscandoHembra(true);
    setError('');
    try {
      const result = await buscarAnimalOffline(buscarIDE.trim(), user.establecimiento_id);
      if (result.animal.sexo !== 'hembra') { setError('Este animal no es hembra.'); return; }
      setHembra(result.animal);
      setFormData(prev => ({ ...prev, hembra_id: result.animal.id }));
      setBuscarIDE('');
    } catch { setError('Animal no encontrado.'); setHembra(null); }
    finally { setBuscandoHembra(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        establecimiento_id: user.establecimiento_id,
        dias_gestacion: formData.dias_gestacion ? parseInt(formData.dias_gestacion) : null,
      };
      if (!payload.fecha_confirmacion) delete payload.fecha_confirmacion;
      if (!payload.toro_id) delete payload.toro_id;
      if (!payload.dias_gestacion) delete payload.dias_gestacion;

      const result = await submitOffline('/api/servicios/', payload);
      setSuccessMessage(result.online ? 'Servicio registrado exitosamente' : result.message);
      setFormData({ hembra_id: '', fecha: new Date().toISOString().split('T')[0], tipo_servicio: 'IATF', toro_id: null, resultado: 'pendiente', fecha_confirmacion: '', dias_gestacion: '', temporada: `${new Date().getFullYear()}-${new Date().getFullYear()+1}`, observaciones: '' });
      setHembra(null);
      if (onSuccess) setTimeout(() => onSuccess(), 2000);
    } catch (err) { setError(err.response?.data?.detail || 'Error al registrar servicio'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Registrar Servicio</h2>
        <div className="flex items-center space-x-2">
          {!navigator.onLine && <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">MODO CAMPO</span>}
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {successMessage}
        </div>
      )}

      {/* Búsqueda de hembra por RFID */}
      {!hembra && (
        <div className="mb-6 p-4 bg-pink-50 border-2 border-pink-200 rounded-lg">
          <h3 className="text-sm font-semibold text-pink-800 mb-3">
            📡 Escanear Hembra para Servicio
          </h3>
          <div className="flex space-x-2">
            <input
              type="text"
              value={buscarIDE}
              onChange={(e) => setBuscarIDE(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarHembraPorRFID()}
              placeholder="Escanea RFID o ingresa VID/IDE..."
              className="flex-1 px-3 py-2 border border-pink-300 rounded-lg font-mono focus:ring-2 focus:ring-pink-500"
              autoFocus
            />
            <button
              onClick={buscarHembraPorRFID}
              disabled={buscandoHembra}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
            >
              {buscandoHembra ? '...' : '🔍'}
            </button>
          </div>
        </div>
      )}

      {/* Información de la hembra */}
      {hembra && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-semibold text-green-800 mb-2">
                ✅ Hembra Seleccionada
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">VID:</span> {hembra.VID}
                </div>
                <div>
                  <span className="font-medium">IDE:</span> {hembra.IDE || 'Sin IDE'}
                </div>
                <div>
                  <span className="font-medium">Categoría:</span> {hembra.categoria}
                </div>
                <div>
                  <span className="font-medium">Raza:</span> {hembra.raza}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setHembra(null);
                setFormData(prev => ({ ...prev, hembra_id: '' }));
              }}
              className="text-red-600 hover:text-red-800"
            >
              Cambiar
            </button>
          </div>
        </div>
      )}

      {/* Formulario de servicio */}
      {hembra && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fecha de servicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Servicio *
              </label>
              <input
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            {/* Tipo de servicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Servicio *
              </label>
              <select
                name="tipo_servicio"
                value={formData.tipo_servicio}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required
              >
                <option value="IATF">IATF (Inseminación Artificial a Tiempo Fijo)</option>
                <option value="IA">IA (Inseminación Artificial)</option>
                <option value="natural">Natural (Toro)</option>
              </select>
            </div>

            {/* Temporada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temporada
              </label>
              <input
                type="text"
                name="temporada"
                value={formData.temporada}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="2025-2026"
              />
            </div>

            {/* Resultado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resultado
              </label>
              <select
                name="resultado"
                value={formData.resultado}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="pendiente">Pendiente (sin confirmar)</option>
                <option value="preñada">Preñada (confirmado)</option>
                <option value="vacia">Vacía (confirmado)</option>
              </select>
            </div>

            {/* Fecha de confirmación (tacto/eco) */}
            {formData.resultado !== 'pendiente' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Confirmación (Tacto/Eco)
                </label>
                <input
                  type="date"
                  name="fecha_confirmacion"
                  value={formData.fecha_confirmacion}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
            )}

            {/* Días de gestación */}
            {formData.resultado === 'preñada' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Días de Gestación Estimados
                </label>
                <input
                  type="number"
                  name="dias_gestacion"
                  value={formData.dias_gestacion}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="280"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Gestación bovina: ~280 días (9 meses)
                </p>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observaciones
            </label>
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Según PDF:</strong> Los servicios deben realizarse entre 14-15 meses para vaquillas con peso {'>'} 250kg a los 12 meses.
            </p>
            <p className="text-xs text-blue-700 mt-1">
              • Temporada de servicio recomendada: 45 días (Etapa 2 del PDF)
            </p>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : navigator.onLine ? 'Registrar Servicio' : 'Guardar Local'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrarServicioForm;
